import { useState, useRef, useEffect } from "react";
import "./index.scss";

const CameraModal = ({ isOpen, onClose, onSave }) => {
  const videoRef = useRef(null);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [streamObj, setStreamObj] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [status, setStatus] = useState(
    "Camera ready. Press Start to begin recording.",
  );
  const [cameraError, setCameraError] = useState(false);

  // Open camera when modal opens
  useEffect(() => {
    if (!isOpen) return;
    let activeStream = null;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        activeStream = s;
        setStreamObj(s);
        if (videoRef.current) videoRef.current.srcObject = s;
        setStatus("Camera ready. Press Start to begin recording.");
        setCameraError(false);
      } catch {
        setStatus(
          "⚠ Camera access denied. Please allow camera permissions and retry.",
        );
        setCameraError(true);
      }
    })();
    return () => {
      if (activeStream) activeStream.getTracks().forEach((t) => t.stop());
    };
  }, [isOpen]);

  const handleClose = () => {
    if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
      mediaRecRef.current.stop();
    }
    if (streamObj) streamObj.getTracks().forEach((t) => t.stop());
    clearInterval(timerRef.current);
    setStreamObj(null);
    setRecording(false);
    setRecorded(false);
    setSeconds(0);
    chunksRef.current = [];
    onClose();
  };

  //   const startRecording = () => {
  //     if (!streamObj) return;
  //     chunksRef.current = [];
  //     const mr = new MediaRecorder(streamObj);
  //     mr.ondataavailable = (e) => {
  //       if (e.data.size > 0) chunksRef.current.push(e.data);
  //     };
  //     mr.start();
  //     mediaRecRef.current = mr;
  //     setRecording(true);
  //     setRecorded(false);
  //     setSeconds(0);
  //     setStatus(""); // show rec-dot via JSX
  //     timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
  //   };

  const startRecording = () => {
    if (!streamObj) return;

    // Reset previous recording
    chunksRef.current = [];
    setRecorded(false);
    setStatus("");
    setSeconds(0);

    // Show camera preview again
    if (videoRef.current) {
      videoRef.current.srcObject = streamObj;
      videoRef.current.controls = false;
    }

    const mr = new MediaRecorder(streamObj);

    mr.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    mr.start();
    mediaRecRef.current = mr;
    setRecording(true);

    timerRef.current = setInterval(() => {
      setSeconds((s) => s + 1);
    }, 1000);
  };

  //   const stopRecording = () => {
  //     if (mediaRecRef.current) mediaRecRef.current.stop();
  //     clearInterval(timerRef.current);
  //     setRecording(false);
  //     setRecorded(true);
  //     setStatus("✓ Recording complete. Review and save.");
  //   };

  const stopRecording = () => {
    if (!mediaRecRef.current) return;

    mediaRecRef.current.onstop = () => {
      const blob = new Blob(chunksRef.current, {
        type: "video/webm",
      });

      // Show recorded video preview
      const videoUrl = URL.createObjectURL(blob);

      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.src = videoUrl;
        videoRef.current.controls = true;
      }

      setRecorded(true);
      setStatus("✓ Recording complete. Review and save.");
    };

    mediaRecRef.current.stop();

    clearInterval(timerRef.current);
    setRecording(false);
  };

  //   const useRecording = () => {
  //     const blob = new Blob(chunksRef.current, { type: "video/webm" });
  //     const url = URL.createObjectURL(blob);
  //     const a = document.createElement("a");
  //     a.href = url;
  //     a.download = "imei-screen-recording.webm";
  //     a.click();
  //     URL.revokeObjectURL(url);
  //     onSave("imei-screen-recording.webm");
  //     handleClose();
  //   };

  const useRecording = () => {
    const blob = new Blob(chunksRef.current, {
      type: "video/webm",
    });

    // Download code commented for now

    /*
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "imei-screen-recording.webm";
      a.click();
      URL.revokeObjectURL(url);
  */

    // Send blob to parent component
    onSave(blob);

    handleClose();
  };
  const fmt = (n) =>
    String(Math.floor(n / 60)).padStart(2, "0") +
    ":" +
    String(n % 60).padStart(2, "0");

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="camera-modal">
        <div className="camera-modal__header">
          <h3>📹 Record Your Screen (IMEI Verification)</h3>
          <button className="camera-modal__close" onClick={handleClose}>
            ✕
          </button>
        </div>

        <video
          ref={videoRef}
          className="camera-modal__video"
          autoPlay
          muted
          playsInline
        />

        <div className="camera-modal__controls">
          {recording && (
            <div className="camera-modal__timer">{fmt(seconds)}</div>
          )}

          <div className="camera-modal__status">
            {recording ? (
              <>
                <span className="rec-dot" />
                Recording…
              </>
            ) : (
              status
            )}
          </div>

          <div className="camera-modal__btns">
            {!recording && !recorded && (
              <button
                className="btn-rec btn-rec--start"
                onClick={startRecording}
                disabled={cameraError}
              >
                ⏺ Start Recording
              </button>
            )}
            {recording && (
              <button className="btn-rec btn-rec--stop" onClick={stopRecording}>
                ⏹ Stop Recording
              </button>
            )}
            {recorded && (
              <button className="btn-rec btn-rec--use" onClick={useRecording}>
                ✓ Use This Recording
              </button>
            )}
          </div>

          <p className="camera-modal__hint">
            Dial *#06# on your phone to display the IMEI, then record
            continuously.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CameraModal;
